import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import OrderedList from "@tiptap/extension-ordered-list";

// Material Dashboard 2 PRO React components
import MDBox from "../../../../../components/MDBox";
import MDTypography from "../../../../../components/MDTypography";
import MDBadge from "../../../../../components/MDBadge";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";
import { formatDate } from "../../../../../utils/formatDate";
import { getImageUrl } from "../../../../../utils/axiosConfig";

// Material Dashboard 2 React context
import { useMaterialUIController } from "../../../../../context";

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },
});

const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      type: {
        default: '1',
        parseHTML: element => element.getAttribute('type'),
        renderHTML: attributes => {
          return {
            type: attributes.type,
          }
        },
      },
    }
  },
});

function SectionViewer({ content }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        orderedList: false,
      }),
      CustomOrderedList.configure({
        keepAttributes: true,
        HTMLAttributes: {
          class: 'ordered-list',
        },
      }),
      Color,
      TextStyle,
      FontSize,
      Image,
      Link.configure({
        openOnClick: true,
      }),
    ],
    content: content || "",
    editable: false,
  });

  useEffect(() => {
    // Only set content if editor is ready and content is valid string/object, not an array
    if (editor && content && !Array.isArray(content)) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return <EditorContent editor={editor} />;
}
SectionViewer.propTypes = {
  content: PropTypes.any,
};

function ArticleInfo({ article }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);

  if (!article || !article.title) return null;

  return (
    <MDBox>
      <MDBox mb={2}>
        <MDTypography variant="h3" fontWeight="bold">
          {article.title}
        </MDTypography>
        {article.subtitle && (
          <MDTypography variant="h5" color="text" fontWeight="regular">
            {article.subtitle}
          </MDTypography>
        )}
      </MDBox>

      <MDBox mb={3} display="flex" alignItems="center" gap={1}>
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
          Estado:
        </MDTypography>
        <MDBadge
          variant="gradient"
          color={article.status === "publicado" ? "success" : "warning"}
          badgeContent={article.status}
          size="xs"
          container
        />
      </MDBox>

      <MDBox mb={3}>
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
          Autor:
        </MDTypography>
        <MDTypography variant="body2" color="text">
          {article.author ? `${article.author.name} ${article.author.last_name}` : "Anónimo"}
        </MDTypography>
      </MDBox>

      <MDBox mb={3}>
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
          Resumen:
        </MDTypography>
        <MDTypography variant="body2" color="text">
          {article.excerpt}
        </MDTypography>
      </MDBox>

      <MDBox mb={3}>
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={1}>
          Estadísticas:
        </MDTypography>
        <MDBox display="flex" gap={3}>
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
              Vistas
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {article.views || 0}
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
              Likes
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {article.likes || 0}
            </MDTypography>
          </MDBox>
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
              Comentarios
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {article.comments?.length || 0}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      <MDBox mb={3}>
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={1}>
          Fechas:
        </MDTypography>
        <MDBox>
          <MDTypography variant="caption" color="text" display="block">
            Creado: {formatDate(article.createdAt)}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            Actualizado: {formatDate(article.updatedAt)}
          </MDTypography>
        </MDBox>
      </MDBox>

      {article.tags && article.tags.length > 0 && (
        <MDBox mb={3}>
          <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={1}>
            Etiquetas:
          </MDTypography>
          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {article.tags.map((tag, index) => (
              <MDBadge
                key={index}
                variant="outlined"
                color="info"
                badgeContent={tag?.name || tag}
                size="xs"
                container
              />
            ))}
          </MDBox>
        </MDBox>
      )}

      {article.sources && article.sources.length > 0 && (
        <MDBox mb={3}>
          <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block" mb={1}>
            Fuentes:
          </MDTypography>
          <MDBox display="flex" flexDirection="column" gap={0.5}>
            {article.sources.map((source, index) => (
              <MDTypography key={index} variant="body2" color="text">
                {source.startsWith("http") ? (
                  <a href={source} target="_blank" rel="noopener noreferrer" style={{ color: '#1A73E8', textDecoration: 'underline' }}>
                    {source}
                  </a>
                ) : (
                  source
                )}
              </MDTypography>
            ))}
          </MDBox>
        </MDBox>
      )}

      {article.feedback && article.status === "borrador" && (
        <MDBox mb={3} p={2} bgColor="error" borderRadius="lg" variant="gradient">
          <MDTypography variant="button" fontWeight="bold" color="white" display="block" mb={1}>
            Feedback del Revisor:
          </MDTypography>
          <MDTypography variant="body2" color="white">
            {article.feedback}
          </MDTypography>
        </MDBox>
      )}

      {article.gallery && article.gallery.length > 0 && (
        <MDBox mb={3}>
          <MDTypography variant="h6" fontWeight="bold" color={darkMode ? "white" : "dark"} mb={2}>
            Galería de Imágenes (Para Revisión)
          </MDTypography>
          <Grid container spacing={2}>
            {article.gallery.map((img, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <MDBox display="flex" flexDirection="column" alignItems="center">
                  <MDBox
                    component="img"
                    src={getImageUrl(img.file || img.url || img)}
                    alt={`${img.section || "Imagen"} ${index + 1}`}
                    width="100%"
                    sx={{ objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd", height: "150px" }}
                  />
                  <MDBadge
                    variant="gradient"
                    color="info"
                    badgeContent={img.section || "Portada"}
                    size="xs"
                    container
                    sx={{ mt: 1 }}
                  />
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Comentarios Section */}
      <MDBox mb={3}>
        <MDBox 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          p={2} 
          sx={{ 
            cursor: "pointer", 
            border: `1px solid ${darkMode ? "#444" : "#eee"}`, 
            borderRadius: "8px",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }
          }}
          onClick={() => setCommentsOpen(!commentsOpen)}
        >
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: darkMode ? "white" : "dark" }}>
            Comentarios ({article.comments?.length || 0})
          </MDTypography>
          <Icon sx={{ color: darkMode ? "white" : "inherit" }}>
            {commentsOpen ? "expand_less" : "expand_more"}
          </Icon>
        </MDBox>
        <Collapse in={commentsOpen}>
          <MDBox p={2}>
            {article.comments && article.comments.length > 0 ? (
              article.comments.map((comment, index) => (
                <MDBox key={index} mb={2} p={2} bgColor={darkMode ? "transparent" : "#f8f9fa"} borderRadius="lg" sx={{ border: darkMode ? "1px solid #444" : "none" }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="button" fontWeight="bold">
                      {comment.user ? comment.user.username : comment.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {formatDate(comment.createdAt)}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text">
                    {comment.content}
                  </MDTypography>
                </MDBox>
              ))
            ) : (
              <MDTypography variant="body2" color="text">
                No hay comentarios aún.
              </MDTypography>
            )}
          </MDBox>
        </Collapse>
      </MDBox>

      {/* Contenido Completo Section */}
      <MDBox mb={3}>
        <MDBox 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          p={2} 
          sx={{ 
            cursor: "pointer", 
            border: `1px solid ${darkMode ? "#444" : "#eee"}`, 
            borderRadius: "8px",
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }
          }}
          onClick={() => setContentOpen(!contentOpen)}
        >
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: darkMode ? "white" : "dark" }}>
            Contenido Completo
          </MDTypography>
          <Icon sx={{ color: darkMode ? "white" : "inherit" }}>
            {contentOpen ? "expand_less" : "expand_more"}
          </Icon>
        </MDBox>
        <Collapse in={contentOpen}>
          <MDBox
            className="tiptap-content"
            sx={{
              backgroundColor: "#fff",
              padding: "16px",
              marginTop: "8px",
              borderRadius: "8px",
              color: "#000",
              "& .ProseMirror": {
                outline: "none",
              },
              "& img": {
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
              },
              "& blockquote": {
                borderLeft: `4px solid ${darkMode ? "#444" : "#ddd"}`,
                paddingLeft: "16px",
                color: darkMode ? "#bbb" : "#666",
              },
              "& ul, & ol": {
                paddingLeft: "24px",
              },
              "& p": {
                marginBottom: "16px",
              },
            }}
          >
            {Array.isArray(article.content) ? (
              article.content.map((section, index) => (
                <MDBox key={index} mb={3}>
                  <SectionViewer content={section.text} />
                  {section.imageIndex !== null && section.imageIndex !== undefined && article.gallery && article.gallery[section.imageIndex] && (
                    <MDBox mt={3} mb={1} display="flex" justifyContent="center">
                      <img 
                        src={getImageUrl(article.gallery[section.imageIndex].file || article.gallery[section.imageIndex].url || article.gallery[section.imageIndex])} 
                        alt={`Imagen asociada a sección ${index + 1}`} 
                        style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain", borderRadius: "8px" }} 
                      />
                    </MDBox>
                  )}
                </MDBox>
              ))
            ) : article.content ? (
              <SectionViewer content={article.content} />
            ) : article.content_html ? (
              <div dangerouslySetInnerHTML={{ __html: article.content_html }} />
            ) : (
              <MDTypography variant="body2" color="text">
                No hay contenido disponible para previsualizar.
              </MDTypography>
            )}
          </MDBox>
        </Collapse>
      </MDBox>
    </MDBox>
  );
}

ArticleInfo.propTypes = {
  article: PropTypes.object.isRequired,
};

export default ArticleInfo;
